/**
 * PreviewPlayerService - Optimized Singleton HLS Preview Player
 *
 * This service provides a global PreviewPlayer instance that avoids destroying
 * HLS instances between previews, with optimized performance for instant startup.
 *
 * Quality modes:
 * - 'thumbnail': Forces lowest quality for hover previews
 * - 'pdp': Allows normal quality for PDP modal previews
 *
 * Global state:
 * - Muted state persists across all preview instances
 * - Default is muted (true)
 * - Stored in localStorage for persistence
 *
 * Performance optimizations:
 * - Synchronous initialization where possible
 * - Reduced async operations
 * - Optimized URL loading with minimal overhead
 * - Better state management
 */

import Hls from 'hls.js';

class PreviewPlayerService {
  // Private fields
  #hls = null;
  #videoElement = null;
  #currentUrl = null;
  #isPlaying = false;
  #isInitialized = false;
  #qualityMode = 'pdp'; // 'thumbnail' or 'pdp'
  #globalMuted = true;
  #callbacks = {
    onPlay: null,
    onPause: null,
    onError: null,
    onReady: null,
    onMutedChange: null,
  };
  #mutedChangeListeners = new Set();
  
  // Transition protection - prevents destructive reset when transitioning between components
  #pendingUrl = null;
  #transitionProtection = false;
  #activeConsumerId = null;

  constructor() {
    this.currentQualityMode = 'thumbnail'; // Default to thumbnail mode
    this.isLoading = false;
    this.error = null;

    // Performance optimizations
    this.initializationPromise = null;
    this.lastLoadTime = 0;
    this.loadTimeout = null;

    // Configuration templates for different quality modes
    this.configTemplates = {
      thumbnail: {
        enableWorker: false, // Disable worker for faster startup
        lowLatencyMode: false,
        backBufferLength: 10, // Reduced for previews
        maxBufferLength: 30, // Reduced for previews
        maxMaxBufferLength: 60, // Reduced for previews
        startLevel: 0, // Force lowest quality
        capLevelToPlayerSize: false, // Don't auto-adjust quality
        debug: false,
        // Performance optimizations
        maxBufferHole: 0.5,
        maxSeekHole: 2,
        maxStarvationDelay: 4,
        maxLoadingDelay: 4,
        // Faster startup
        manifestLoadingTimeOut: 10000,
        manifestLoadingMaxRetry: 1,
        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 1,
        fragLoadingTimeOut: 20000,
        fragLoadingMaxRetry: 1,
        // Force lowest quality for thumbnails
        abrEwmaDefaultEstimate: 500000, // 500kbps default estimate
        abrBandWidthFactor: 0.95, // Conservative bandwidth estimation
        abrBandWidthUpFactor: 0.7, // Even more conservative for upward switching
        abrMaxWithRealBitrate: false, // Don't use real bitrate for ABR decisions
        // Disable quality switching during preview
        abrEwmaFastLive: 3.0,
        abrEwmaSlowLive: 9.0,
        abrEwmaFastVoD: 3.0,
        abrEwmaSlowVoD: 9.0,
        // Force specific quality level
        testBandwidth: false, // Don't test bandwidth
        progressive: false, // Don't use progressive loading
        // Additional quality forcing
        maxLevelCappingMode: 'downscale', // Force downscaling
        levelCappingErrorMargin: 1.5, // Conservative error margin
        // Disable adaptive bitrate switching
        abrEwmaDefaultEstimate: 500000, // Low default estimate
        abrBandWidthFactor: 0.8, // Very conservative
        abrBandWidthUpFactor: 0.5, // Very conservative upward
        abrMaxWithRealBitrate: false,
        // Force lowest level
        startLevel: 0, // Always start at lowest quality
        levelLoadingTimeOut: 5000, // Faster timeout for low quality
        levelLoadingMaxRetry: 1,
        fragLoadingTimeOut: 10000, // Faster timeout for fragments
        fragLoadingMaxRetry: 1,
      },
      pdp: {
        enableWorker: true, // Enable worker for better performance
        lowLatencyMode: false,
        backBufferLength: 30, // Normal buffer for PDP
        maxBufferLength: 60, // Normal buffer for PDP
        maxMaxBufferLength: 120, // Normal buffer for PDP
        startLevel: -1, // Auto quality selection
        capLevelToPlayerSize: true, // Auto-adjust quality
        debug: false,
        // Normal performance settings
        maxBufferHole: 0.5,
        maxSeekHole: 2,
        maxStarvationDelay: 4,
        maxLoadingDelay: 4,
        // Normal timeouts
        manifestLoadingTimeOut: 10000,
        manifestLoadingMaxRetry: 2,
        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 2,
        fragLoadingTimeOut: 20000,
        fragLoadingMaxRetry: 2,
        // Normal ABR settings
        abrEwmaDefaultEstimate: 500000,
        abrBandWidthFactor: 0.95,
        abrBandWidthUpFactor: 0.7,
        abrMaxWithRealBitrate: false,
        // Normal quality switching
        abrEwmaFastLive: 3.0,
        abrEwmaSlowLive: 9.0,
        abrEwmaFastVoD: 3.0,
        abrEwmaSlowVoD: 9.0,
        // Normal settings
        testBandwidth: true,
        progressive: false,
        maxLevelCappingMode: 'downscale',
        levelCappingErrorMargin: 1.5,
      },
    };

    // Initialize immediately
    this.initialize();
  }

  /**
   * Initialize the global video element and HLS instance (synchronous where possible)
   */
  initialize() {
    if (this.#isInitialized || this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = new Promise((resolve, reject) => {
      try {
        // Create video element immediately
        if (!this.#videoElement) {
          this.#videoElement = document.createElement('video');
          this.#videoElement.className = 'preview-player-video';
          this.#videoElement.muted = this.#globalMuted; // Apply persisted muted state
          this.#videoElement.playsInline = true;
          this.#videoElement.preload = 'metadata';
          this.#videoElement.autoplay = false; // Let us control this

          // Set up video event listeners
          this.setupVideoEventListeners();
        }

        // Initialize HLS instance if supported
        if (Hls.isSupported() && !this.#hls) {
          this.#hls = new Hls(this.configTemplates[this.currentQualityMode]);
          this.setupHLSEventListeners();
          this.#hls.attachMedia(this.#videoElement);
        } else if (
          !Hls.isSupported() &&
          this.#videoElement.canPlayType('application/vnd.apple.mpegurl')
        ) {
        } else {
          throw new Error('HLS not supported in this browser');
        }

        this.#isInitialized = true;

        resolve();
      } catch (error) {
        console.error('PreviewPlayerService: Initialization failed:', error);
        this.error = error;
        reject(error);
      }
    });

    return this.initializationPromise;
  }

  /**
   * Set up video element event listeners
   */
  setupVideoEventListeners() {
    this.#videoElement.addEventListener('play', () => {
      this.#isPlaying = true;
      if (this.#callbacks.onPlay) {
        this.#callbacks.onPlay();
      }
    });

    this.#videoElement.addEventListener('pause', () => {
      this.#isPlaying = false;
      if (this.#callbacks.onPause) {
        this.#callbacks.onPause();
      }
    });

    this.#videoElement.addEventListener('timeupdate', () => {
      this.currentTime = this.#videoElement.currentTime;
      if (this.#callbacks.onTimeUpdate) {
        this.#callbacks.onTimeUpdate(this.currentTime);
      }
    });

    this.#videoElement.addEventListener('ended', () => {
      this.#isPlaying = false;
      if (this.#callbacks.onEnd) {
        this.#callbacks.onEnd();
      }
    });

    this.#videoElement.addEventListener('loadedmetadata', () => {
      this.duration = this.#videoElement.duration;
      // Don't set start time here - let the onReady callback handle all seeking
    });

    this.#videoElement.addEventListener('error', e => {
      console.error('PreviewPlayerService: Video error:', e);
      this.error = 'Video loading failed';
      if (this.#callbacks.onError) {
        this.#callbacks.onError(e);
      }
    });
  }

  /**
   * Set up HLS event listeners
   */
  setupHLSEventListeners() {
    if (!this.#hls) return;

    this.#hls.on(Hls.Events.MANIFEST_PARSED, () => {
      this.error = null;
      this.isLoading = false;

      // Apply quality mode specific behavior
      if (this.currentQualityMode === 'thumbnail') {
        this.forceLowestQuality();
      } else {
        this.enableNormalQuality();
      }

      if (this.#callbacks.onReady) {
        this.#callbacks.onReady();
      }
    });

    this.#hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
      // Only force lowest quality in thumbnail mode
      if (this.currentQualityMode === 'thumbnail' && data.level > 0) {
        this.forceLowestQuality();
      }
    });

    this.#hls.on(Hls.Events.ERROR, (event, data) => {
      // Safari-specific: Handle buffer seek over hole errors more gracefully
      if (data.details === 'bufferSeekOverHole') {
        console.warn('PreviewPlayerService: Safari buffer seek over hole (non-fatal) - attempting recovery');
        
        // Try to recover by seeking to a slightly different position
        if (this.#videoElement && !isNaN(this.#videoElement.duration)) {
          const currentTime = this.#videoElement.currentTime || 0;
          const seekTime = Math.min(currentTime + 0.1, this.#videoElement.duration - 0.1);
          
          try {
            this.#videoElement.currentTime = seekTime;
            return; // Don't log as error since we handled it
          } catch (seekError) {
            console.warn('PreviewPlayerService: Seek recovery failed:', seekError);
          }
        }
        return; // Don't treat buffer holes as errors
      }

      // Log other errors normally
      console.error('PreviewPlayerService: HLS error:', data);

      if (data.fatal) {
        this.error = `HLS Error: ${data.details}`;
        this.isLoading = false;

        if (this.#callbacks.onError) {
          this.#callbacks.onError(data);
        }
      }
    });
  }

  /**
   * Set the quality mode for the preview player
   * @param {string} mode - 'thumbnail' for low quality, 'pdp' for normal quality
   */
  setQualityMode(mode) {
    if (!['thumbnail', 'pdp'].includes(mode)) {
      console.warn('PreviewPlayerService: Invalid quality mode:', mode);
      return;
    }

    if (this.currentQualityMode === mode) {
      return;
    }

    this.currentQualityMode = mode;

    // If HLS instance exists, we need to recreate it with new config
    if (this.#hls) {
      this.recreateHLSInstance();
    }
  }

  /**
   * Recreate HLS instance with new quality mode configuration
   */
  recreateHLSInstance() {
    if (!this.#hls) return;

    // Store current state
    const wasPlaying = this.#isPlaying;
    const currentUrl = this.#currentUrl;

    // Destroy current instance
    this.#hls.destroy();
    this.#hls = null;

    // Create new instance with new config
    if (Hls.isSupported()) {
      this.#hls = new Hls(this.configTemplates[this.currentQualityMode]);
      this.setupHLSEventListeners();
      this.#hls.attachMedia(this.#videoElement);

      // Reload current URL if it exists
      if (currentUrl) {
        this.loadUrl(currentUrl, { autoPlay: wasPlaying });
      }
    }
  }

  /**
   * Force the HLS player to use the lowest quality level (thumbnail mode only)
   */
  forceLowestQuality() {
    if (!this.#hls || this.currentQualityMode !== 'thumbnail') return;

    try {
      const levels = this.#hls.levels;
      if (levels && levels.length > 0) {
        // Find the lowest quality level (usually index 0)
        const lowestLevel = 0;

        // Set the current level to the lowest
        this.#hls.currentLevel = lowestLevel;
      }
    } catch (error) {
      console.warn('PreviewPlayerService: Failed to force lowest quality:', error);
    }
  }

  /**
   * Enable normal quality switching (PDP mode only)
   */
  enableNormalQuality() {
    if (!this.#hls || this.currentQualityMode !== 'pdp') return;

    try {
      // Enable auto level switching by setting to -1 (auto)
      this.#hls.currentLevel = -1;
    } catch (error) {
      console.warn('PreviewPlayerService: Failed to enable normal quality:', error);
    }
  }

  /**
   * Load a new HLS URL (optimized for speed)
   * @param {string} hlsUrl - The HLS URL to load
   * @param {Object} options - Loading options
   * @param {string} options.consumerId - Optional ID to identify the consumer (e.g., 'thumbnail-hover', 'pdp-modal')
   */
  loadUrl(hlsUrl, options = {}) {
    if (!hlsUrl) {
      console.warn('PreviewPlayerService: No URL provided');
      return Promise.resolve();
    }

    // If it's the same URL, just return immediately
    if (this.#currentUrl === hlsUrl) {
      return Promise.resolve();
    }

    // Debounce rapid URL changes
    const now = Date.now();
    if (now - this.lastLoadTime < 100) {
      // 100ms debounce

      return Promise.resolve();
    }
    this.lastLoadTime = now;

    // Clear any pending load timeout
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }
    
    // Set pending URL and enable transition protection
    // This prevents destructive resets from old consumers during transitions
    this.#pendingUrl = hlsUrl;
    this.#transitionProtection = true;
    if (options.consumerId) {
      this.#activeConsumerId = options.consumerId;
    }

    return new Promise((resolve, reject) => {
      try {
        this.isLoading = true;
        this.error = null;
        this.#currentUrl = hlsUrl;

        // Ensure initialization is complete
        if (!this.#isInitialized) {
          this.initialize()
            .then(() => {
              this.performLoad(hlsUrl, options, resolve, reject);
            })
            .catch(reject);
        } else {
          this.performLoad(hlsUrl, options, resolve, reject);
        }
      } catch (error) {
        console.error('PreviewPlayerService: Error loading URL:', error);
        this.error = error.message;
        this.isLoading = false;
        reject(error);
      }
    });
  }

  /**
   * Perform the actual URL loading
   */
  performLoad(hlsUrl, options, resolve, reject) {
    try {
      if (this.#hls) {
        // Use HLS.js - much faster than recreating
        this.#hls.loadSource(hlsUrl);

        // Apply quality mode specific behavior after loading
        setTimeout(() => {
          if (this.currentQualityMode === 'thumbnail') {
            this.forceLowestQuality();
          } else {
            this.enableNormalQuality();
          }
        }, 100);
      } else if (this.#videoElement.canPlayType('application/vnd.apple.mpegurl')) {
        // Use native HLS
        this.#videoElement.src = hlsUrl;
        this.#videoElement.load();
      } else {
        throw new Error('No HLS support available');
      }

      // Auto-play if requested
      if (options.autoPlay) {
        // Longer delay to ensure loading has started and prevent race conditions
        this.loadTimeout = setTimeout(async () => {
          try {
            // Check if we're still loading the same URL (prevent race conditions)
            if (this.#currentUrl === hlsUrl) {
              await this.play();
              this.isLoading = false; // Set loading to false when successful
              // Clear transition protection after successful load
              this.#pendingUrl = null;
              this.#transitionProtection = false;
              resolve();
            } else {
              this.isLoading = false; // Set loading to false when successful
              resolve();
            }
          } catch (error) {
            // Don't reject for abort errors, just resolve
            if (error.name === 'AbortError') {
              this.isLoading = false; // Set loading to false when successful
              resolve();
            } else {
              reject(error);
            }
          }
        }, 200); // Increased delay to prevent race conditions
      } else {
        this.isLoading = false; // Set loading to false when successful
        // Clear transition protection after successful load
        this.#pendingUrl = null;
        this.#transitionProtection = false;
        resolve();
      }
    } catch (error) {
      this.error = error.message;
      this.isLoading = false;
      // Clear transition protection on error
      this.#pendingUrl = null;
      this.#transitionProtection = false;
      reject(error);
    }
  }

  /**
   * Play the video
   */
  async play() {
    if (!this.#videoElement) return;

    try {
      // Check if video is already playing to avoid unnecessary play calls
      if (!this.#videoElement.paused) {
        return;
      }

      // Check if video is ready to play
      if (this.#videoElement.readyState < 2) {
        // HAVE_CURRENT_DATA

        // Wait for video to be ready
        await new Promise(resolve => {
          const handleCanPlay = () => {
            this.#videoElement.removeEventListener('canplay', handleCanPlay);
            resolve();
          };
          this.#videoElement.addEventListener('canplay', handleCanPlay);

          // Timeout after 5 seconds
          setTimeout(() => {
            this.#videoElement.removeEventListener('canplay', handleCanPlay);
            resolve();
          }, 5000);
        });
      }

      // Attempt to play with error handling
      const playPromise = this.#videoElement.play();

      if (playPromise !== undefined) {
        await playPromise;
      }
    } catch (error) {
      // Handle specific abort error (interrupted by pause)
      if (error.name === 'AbortError') {
        // Don't throw this error as it's expected behavior
        return;
      }

      console.warn('PreviewPlayerService: Play failed:', error);
      throw error;
    }
  }

  /**
   * Pause the video
   */
  pause() {
    if (this.#videoElement) {
      this.#videoElement.pause();
    }
  }

  /**
   * Set the muted state of the video
   * @param {boolean} muted - Whether the video should be muted
   */
  setMuted(muted) {
    if (this.#videoElement) {
      this.#videoElement.muted = muted;
    }

    this.#globalMuted = muted;

    // Persist the muted state
    this.setPersistedMutedState(muted);

    // Notify all listeners of muted state change
    this.#notifyMutedChangeListeners(muted);
  }

  /**
   * Toggle muted state globally
   */
  toggleMuted() {
    const newMutedState = !this.#globalMuted;
    this.setMuted(newMutedState);
    return newMutedState;
  }

  /**
   * Get current muted state
   */
  getMuted() {
    return this.#globalMuted;
  }

  /**
   * Seek to specific time
   */
  seek(time) {
    if (this.#videoElement && !this.isLoading) {
      this.#videoElement.currentTime = time;

      // Add a check to see if the seek actually worked
      setTimeout(() => {
        const actualTime = this.#videoElement.currentTime;
      }, 100);
    }
  }

  /**
   * Get current video element
   */
  getVideoElement() {
    return this.#videoElement;
  }

  /**
   * Set event callbacks
   */
  setCallbacks(callbacks = {}) {
    this.#callbacks.onReady = callbacks.onReady;
    this.#callbacks.onError = callbacks.onError;
    this.#callbacks.onPlay = callbacks.onPlay;
    this.#callbacks.onPause = callbacks.onPause;
    this.#callbacks.onTimeUpdate = callbacks.onTimeUpdate;
    this.#callbacks.onEnd = callbacks.onEnd;
    this.#callbacks.onMutedChange = callbacks.onMutedChange;
  }

  /**
   * Get current callbacks
   */
  getCallbacks() {
    return {
      onReady: this.#callbacks.onReady,
      onError: this.#callbacks.onError,
      onPlay: this.#callbacks.onPlay,
      onPause: this.#callbacks.onPause,
      onTimeUpdate: this.#callbacks.onTimeUpdate,
      onEnd: this.#callbacks.onEnd,
      onMutedChange: this.#callbacks.onMutedChange,
    };
  }

  /**
   * Get current state
   */
  getState() {
    return {
      isInitialized: this.#isInitialized,
      isLoading: this.isLoading,
      isPlaying: this.#isPlaying,
      isMuted: this.#globalMuted,
      currentTime: this.currentTime,
      duration: this.duration,
      currentUrl: this.#currentUrl,
      error: this.error,
    };
  }

  /**
   * Reset the player (for cleanup)
   * @param {Object} options - Reset options
   * @param {boolean} options.force - Force reset even if transition protection is active
   * @param {string} options.consumerId - ID of the consumer requesting reset (for transition coordination)
   */
  reset(options = {}) {
    const { force = false, consumerId = null } = options;
    
    // Check if transition protection is active (another component is taking over)
    // Skip reset if:
    // 1. Transition protection is active AND
    // 2. We're not forcing reset AND
    // 3. A different URL is pending (meaning another component is loading)
    if (this.#transitionProtection && !force && this.#pendingUrl) {
      // Allow reset if the same consumer is requesting it
      if (consumerId && consumerId === this.#activeConsumerId) {
        // Same consumer, allow reset
      } else {
        // Different consumer or no consumer ID - skip reset to prevent race condition
        console.log('PreviewPlayerService: Skipping reset - transition protection active (another component is loading)');
        return;
      }
    }
    
    // Clear any pending timeouts
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }

    // Pause video if playing
    if (this.#videoElement && !this.#videoElement.paused) {
      try {
        this.#videoElement.pause();
      } catch (error) {
        console.warn('PreviewPlayerService: Error pausing video during reset:', error);
      }
    }

    this.pause();
    this.#currentUrl = null;
    this.error = null;
    this.isLoading = false;
    this.currentTime = 0;
    this.duration = 0;
    
    // Clear transition protection on full reset
    this.#pendingUrl = null;
    this.#transitionProtection = false;
    this.#activeConsumerId = null;

    // Clear callbacks
    this.setCallbacks({});
  }

  /**
   * Cleanup and destroy (only call when completely done with previews)
   */
  destroy() {
    this.reset();

    if (this.#hls) {
      this.#hls.destroy();
      this.#hls = null;
    }

    if (this.#videoElement) {
      this.#videoElement.remove();
      this.#videoElement = null;
    }

    this.#isInitialized = false;
    this.initializationPromise = null;
  }

  /**
   * Get persisted muted state
   */
  getPersistedMutedState() {
    try {
      const persistedMuted = localStorage.getItem('previewPlayerMuted');
      // Default to true (muted) if no value is stored
      return persistedMuted === null ? true : persistedMuted === 'true';
    } catch (error) {
      console.warn('PreviewPlayerService: Failed to get persisted muted state:', error);
      return true; // Default to muted
    }
  }

  /**
   * Set persisted muted state
   */
  setPersistedMutedState(muted) {
    try {
      localStorage.setItem('previewPlayerMuted', muted.toString());
    } catch (error) {
      console.warn('PreviewPlayerService: Failed to set persisted muted state:', error);
    }
  }

  // Add event listener for muted state changes
  addMutedChangeListener(listener) {
    this.#mutedChangeListeners.add(listener);
  }

  // Remove event listener for muted state changes
  removeMutedChangeListener(listener) {
    this.#mutedChangeListeners.delete(listener);
  }

  // Notify all listeners of muted state change
  #notifyMutedChangeListeners(muted) {
    this.#mutedChangeListeners.forEach(listener => {
      try {
        listener(muted);
      } catch (error) {
        console.error('Error in muted change listener:', error);
      }
    });
  }
}

// Create singleton instance
export const previewPlayerService = new PreviewPlayerService();

// Export for debugging
if (typeof window !== 'undefined') {
  window.previewPlayerService = previewPlayerService;
}
